import dayjs from 'dayjs';

import i18n from 'i18next';

import { modes } from 'editor/components/SideBar/Qualify/limitPlayTimes/periodPlay/PeriodPlayModeSelector';
import { timezoneList } from 'config/timezone';

export function periodTimeUtcToTimeZone(timezone, timeArrays) {
    const offset =
        timezoneList.find(tz => String(tz.tzCode).toLowerCase() === String(timezone).toLowerCase())
            ?.utc || '+00:00';
    const method = offset[0] === '+' ? (a, b) => a + b : (a, b) => a - b;
    return offsetHandler(offset, timeArrays, method);
}

export function periodTimeTimezoneToUtc(timezone, timeArrays = []) {
    const offset =
        timezoneList.find(tz => String(tz.tzCode).toLowerCase() === String(timezone).toLowerCase())
            ?.utc || '+00:00';
    const method = offset[0] === '+' ? (a, b) => a - b : (a, b) => a + b;
    return offsetHandler(offset, timeArrays, method);
}

export function offsetHandler(offset, timeArrays = [], calcMethod) {
    return timeArrays.map(t => {
        const obj = { start: { ...t.start }, end: { ...t.end } };
        const start = calculateOffset(offset, t.start.time, calcMethod);
        const end = calculateOffset(offset, t.end.time, calcMethod);
        obj.start.time = trimSecond(start.timeStr);
        obj.end.time = trimSecond(end.timeStr);
        if (obj.start.weekday) {
            obj.start.weekday = clampWeekDay(obj.start.weekday + start.dayoffset);
        }
        if (obj.end.weekday) {
            obj.end.weekday = clampWeekDay(obj.end.weekday + end.dayoffset);
        }
        return obj;
    });
}

export function calculateOffset(offset, timeStr, calcMethod) {
    const offsetHour = offset.substring(1, 3);
    const offsetMin = offset.substring(4, 6);
    const timeHour = timeStr.substring(0, 2);
    const timeMin = timeStr.substring(3, 5);
    const timeSec = timeStr.substring(6, 8);

    let timeMinWithOffset = calcMethod(Number(timeMin), Number(offsetMin));
    const minCauseHourOffset = (() => {
        if (timeMinWithOffset > 59) return 1;
        else if (timeMinWithOffset < 0) return -1;
        else return 0;
    })();

    let timeHourWithOffset = calcMethod(Number(timeHour), Number(offsetHour)) + minCauseHourOffset;

    const dayoffset = (() => {
        if (timeHourWithOffset < 0) return -1;
        else if (timeHourWithOffset / 24 >= 1) return 1;
        else return 0;
    })();

    timeHourWithOffset = timeHourWithOffset < 0 ? timeHourWithOffset + 24 : timeHourWithOffset % 24;
    timeMinWithOffset = timeMinWithOffset < 0 ? timeMinWithOffset + 60 : timeMinWithOffset % 60;

    return {
        timeStr: `${String(timeHourWithOffset).padStart(2, '0')}:${String(
            timeMinWithOffset
        ).padStart(2, '0')}${Boolean(timeSec) ? `:${timeSec}` : ''}`,
        dayoffset
    };
}

function trimSecond(timeStr) {
    return `${timeStr.substring(0, 5)}:00`;
}

function clampWeekDay(weekday) {
    if (weekday > 7) {
        return 1;
    } else if (weekday < 1) {
        return 7;
    } else {
        return weekday;
    }
}

export function hasNotPlayedInPeriod({ rangeStart, rangeEnd, current, lastEntries } = {}) {
    return (
        rangeStart <= current &&
        current <= rangeEnd &&
        !(rangeStart <= lastEntries && lastEntries <= rangeEnd)
    );
}

export function generatePeriodTimeMatchInfo(
    isPeriodPlayMode,
    { timezone, mode = modes.DAY, periodTimes = [] } = {}
) {
    if (!isPeriodPlayMode) {
        return {
            match: true,
            periodStartInTZ: '',
            periodEndInTZ: '',
            currentInTZ: ''
        };
    }

    const currentInTimezone = dayjs().tz(timezone);
    const currentInTimezoneDate = currentInTimezone.format('YYYY-MM-DD');
    const currentInTimezoneTime = currentInTimezone.format('HH:mm:ss');
    const periodTimeInTimeZone = periodTimeUtcToTimeZone(timezone, JSON.parse(periodTimes));
    const currentInTZ = `${currentInTimezoneDate} ${currentInTimezoneTime}`;

    if (mode === modes.DAY) {
        const matchedPeriod = periodTimeInTimeZone.find(
            ({ start, end }) =>
                currentInTimezoneTime >= start.time && currentInTimezoneTime < end.time
        );
        if (matchedPeriod) {
            return {
                match: true,
                periodStartInTZ: `${currentInTimezoneDate} ${matchedPeriod.start.time}`,
                periodEndInTZ: `${currentInTimezoneDate} ${matchedPeriod.end.time}`,
                currentInTZ
            };
        }
    } else if (mode === modes.WEEK) {
        const currentInTimezoneWeekDay = currentInTimezone.isoWeekday();
        const matchedPeriod = periodTimeInTimeZone.find(({ start, end }) => {
            const isSpanWeek = start.weekday > end.weekday;
            const isWeekdayInRange = isSpanWeek
                ? !(
                      end.weekday < currentInTimezoneWeekDay &&
                      currentInTimezoneWeekDay < start.weekday
                  )
                : start.weekday <= currentInTimezoneWeekDay &&
                  currentInTimezoneWeekDay <= end.weekday;
            const isTimeInRange =
                (currentInTimezoneWeekDay !== start.weekday ||
                    currentInTimezoneTime >= start.time) &&
                (currentInTimezoneWeekDay !== end.weekday || currentInTimezoneTime < end.time);
            return isWeekdayInRange && isTimeInRange;
        });

        if (matchedPeriod) {
            const startWeekdayOffset = (() => {
                const o = currentInTimezoneWeekDay - matchedPeriod.start.weekday;
                return o < 0 ? o + 7 : o;
            })();
            const endWeekdayOffset = (() => {
                const o = matchedPeriod.end.weekday - currentInTimezoneWeekDay;
                return o < 0 ? o + 7 : o;
            })();
            const periodStartInTZ = `${currentInTimezone
                .subtract(startWeekdayOffset, 'day')
                .format('YYYY-MM-DD')} ${matchedPeriod.start.time}`;
            const periodEndInTZ = `${currentInTimezone
                .add(endWeekdayOffset, 'day')
                .format('YYYY-MM-DD')} ${matchedPeriod.end.time}`;

            return {
                match: true,
                periodStartInTZ,
                periodEndInTZ,
                currentInTZ
            };
        }
    }

    return { match: false, periodStartInTZ: '', periodEndInTZ: '', currentInTZ };
}

export const errorMessage = {
    EMPTY: () => i18n.t('label.period_play.error.required'),
    SMALLER_END_DATE: () => i18n.t('label.period_play.error.smaller_end'),
    OVERLAP_PERIOD: () => i18n.t('label.period_play.error.overlap'),
    EXCEED_WEEK_PERIOD: () => i18n.t('label.period_play.error.exceed_week')
};

export function isOverLapTime(a, b) {
    return a?.start?.time < b?.end?.time && b?.start?.time < a?.end?.time;
}

export function checkEmptyDatetimeError(datetimeArray = []) {
    let hasEmptyError = false;
    let errorObj = {};
    datetimeArray.forEach((t, index) => {
        errorObj[index] = {};
        if (t.start.time === null) {
            hasEmptyError = true;
            errorObj[index].start = true;
        }
        if (t.end.time === null) {
            hasEmptyError = true;
            errorObj[index].end = true;
        }
    });
    if (hasEmptyError) {
        return {
            message: errorMessage.EMPTY(),
            items: errorObj
        };
    }
}

export function checkSmallerEndDateError(datetimeArray = []) {
    let hasInvalidPeriodError = false;
    let errorObj = {};
    datetimeArray.forEach((t, index) => {
        if (t.start.time >= t.end.time) {
            hasInvalidPeriodError = true;
            errorObj[index] = { start: true, end: true };
        }
    });
    if (hasInvalidPeriodError) {
        return {
            message: errorMessage.SMALLER_END_DATE(),
            items: errorObj
        };
    }
}

export function checkOverlapPeriodError(datetimeArray = []) {
    let hasOverLapError = false;
    let errorObj = {};
    for (let [i, t] of datetimeArray.entries()) {
        for (let [j, otherT] of datetimeArray.entries()) {
            if (i !== j) {
                if (isOverLapTime(t, otherT)) {
                    hasOverLapError = true;
                    errorObj[i] = { start: true, end: true };
                    errorObj[j] = { start: true, end: true };
                    break;
                }
            }
        }
    }
    if (hasOverLapError) {
        return {
            message: errorMessage.OVERLAP_PERIOD(),
            items: errorObj
        };
    }
}

export function checkExceedWeekPeriodError(datetimeArray = []) {
    let hasExceedWeekPeriodError = false;
    let errorObj = {};
    datetimeArray.forEach((t, index) => {
        if (t.start.weekday === t.end.weekday && t.end.time >= t.start.time) {
            hasExceedWeekPeriodError = true;
            errorObj[index] = { start: true, end: true };
        }
    });
    if (hasExceedWeekPeriodError) {
        return {
            message: errorMessage.EXCEED_WEEK_PERIOD(),
            items: errorObj
        };
    }
}
